import { Content, Portal, Root, Trigger } from '@radix-ui/react-popover';
import clsx from 'clsx';
import type { ReactElement, ReactNode, Ref, RefObject } from 'react';
import { useEffect, useRef, useState } from 'react';

import { Badge } from '../../core/badge/Badge';
import { Button } from '../../core/button/Button';
import { useColorSchemeScope } from '../../core/color-scheme/ColorSchemeScope';
import {
    CommandPalette,
    CommandPaletteEmpty,
    CommandPaletteFooter,
    CommandPaletteGroup,
    CommandPaletteItem,
    CommandPaletteList,
    CommandPaletteSearch,
} from '../../core/command-palette/CommandPalette';
import { IconChevronGrabberVertical, IconPlus } from '../../core/icons';
import { CheckIcon } from '../../core/icons/CheckIcon';
import { floatingSurfaceClassNames } from '../../core/surface/surfaceClasses';
import type { JobArchitectureStatistics } from './JobArchitectureStats';
import { JobArchitectureStats } from './JobArchitectureStats';
import { observeVisibleArchitectures } from './observe-visible-architectures';

export type { JobArchitectureStatistics } from './JobArchitectureStats';

type TriggerSize = 'l' | 'm';

export type JobArchitecture<TStatistics = JobArchitectureStatistics> = {
    readonly badge?: string;
    readonly id: string;
    readonly statistics?: TStatistics;
    readonly title: string;
};

export type JobArchitectureSelectLabels = {
    readonly all: string;
    readonly current: string;
    readonly empty: string;
    readonly search: string;
};

const defaultLabels: JobArchitectureSelectLabels = {
    all: 'All architectures',
    current: 'Current',
    empty: 'No results found',
    search: 'Search',
};

type JobArchitectureSelectProps<TStatistics> = {
    readonly architectures: readonly JobArchitecture<TStatistics>[];
    readonly ariaDescribedBy?: string;
    readonly ariaLabel: string;
    readonly createNewArchitecture?: string;
    readonly currentArchitecture?: JobArchitecture<TStatistics>;
    /** Disables the trigger, so the switcher opens neither on click nor from the keyboard and dims to 50 %. */
    readonly disabled?: boolean;
    readonly getItemStatsContent?: (id: string) => ReactNode;
    readonly onVisibleArchitectureIdsChange?: (ids: readonly string[]) => void;
    readonly getStatsContent: (data: TStatistics | undefined) => ReactNode;
    /** Leaves the statistics rows out even when the architectures carry statistics. */
    readonly hideStatistics?: boolean;
    /** Keeps the statistics on the list rows but drops them from the trigger. */
    readonly hideTriggerStatistics?: boolean;
    readonly labels?: Partial<JobArchitectureSelectLabels>;
    readonly onCreateNewArchitecture?: () => void;
    readonly onOpenChange?: (open: boolean) => void;
    readonly onSwitchArchitecture: (id: string) => void;
    readonly open?: boolean;
    readonly placeholder?: string;
    readonly size?: TriggerSize;
    readonly triggerBackgroundColor?: string;
    /** Extra classes for the `button` trigger variant. */
    readonly triggerClassName?: string;
    readonly triggerRef?: Ref<HTMLButtonElement>;
    /**
     * `card` shows the title and the statistics in a surface; `button` is a small secondary button with the title
     * alone, for a header that shows the statistics elsewhere.
     */
    readonly triggerVariant?: 'button' | 'card';
};

const VisibleArchitectures = ({
    commandRef,
    onChange,
}: {
    readonly commandRef: RefObject<HTMLDivElement | null>;
    readonly onChange: (ids: readonly string[]) => void;
}): null => {
    useEffect(() => {
        const list = commandRef.current?.querySelector<HTMLElement>('[cmdk-list]');
        if (list === undefined || list === null) return;

        return observeVisibleArchitectures(list, onChange);
    }, [commandRef, onChange]);

    return null;
};

const SEARCH_THRESHOLD = 3;

// Items are keyed by id, so match the title keyword only, as a plain substring.
const matchesTitle = (_value: string, search: string, keywords?: readonly string[]): number => {
    const query = search.trim().toLocaleLowerCase();
    return keywords?.some((keyword) => keyword.toLocaleLowerCase().includes(query)) === true ? 1 : 0;
};

export const JobArchitectureSelect = <TStatistics = JobArchitectureStatistics>({
    architectures,
    ariaDescribedBy,
    ariaLabel,
    createNewArchitecture = 'Create new architecture',
    currentArchitecture,
    disabled = false,
    getStatsContent,
    getItemStatsContent,
    onVisibleArchitectureIdsChange,
    hideStatistics = false,
    hideTriggerStatistics = false,
    labels: labelOverrides,
    onCreateNewArchitecture = undefined,
    onOpenChange,
    onSwitchArchitecture,
    open,
    placeholder = 'Select Architecture',
    size = 'm',
    triggerBackgroundColor,
    triggerClassName,
    triggerRef,
    triggerVariant = 'card',
}: JobArchitectureSelectProps<TStatistics>): ReactElement => {
    const colorScheme = useColorSchemeScope();
    const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
    const isOpen = open ?? uncontrolledOpen;
    const labels = { ...defaultLabels, ...labelOverrides };
    const commandRef = useRef<HTMLDivElement>(null);
    const isSearchable = architectures.length > SEARCH_THRESHOLD;

    const setOpen = (next: boolean): void => {
        setUncontrolledOpen(next);
        onOpenChange?.(next);
    };

    const current = architectures.find((architecture) => architecture.id === currentArchitecture?.id);
    const showsTriggerStats =
        !hideStatistics && !hideTriggerStatistics && currentArchitecture?.statistics !== undefined;
    const others = architectures.filter((architecture) => architecture.id !== current?.id);

    const selectArchitecture = (id: string): void => {
        setOpen(false);
        if (id !== current?.id) {
            onSwitchArchitecture(id);
        }
    };

    const renderItem = (architecture: JobArchitecture<TStatistics>, isCurrent: boolean): ReactElement => (
        <CommandPaletteItem
            className="flex-col items-stretch gap-xs"
            data-architecture-id={architecture.id}
            key={architecture.id}
            keywords={[architecture.title]}
            value={architecture.id}
            onSelect={selectArchitecture}
        >
            <span className="flex items-center gap-m">
                <span className="flex-1 min-w-0 text-pretty">{architecture.title}</span>
                {architecture.badge !== undefined && (
                    <Badge className="shrink-0" size="sm" variant="gray">
                        {architecture.badge}
                    </Badge>
                )}
                {isCurrent && (
                    <span className="inline-flex shrink-0 items-center text-text-form">
                        <CheckIcon />
                    </span>
                )}
            </span>
            {!hideStatistics && getItemStatsContent !== undefined
                ? getItemStatsContent(architecture.id)
                : !hideStatistics &&
                  architecture.statistics !== undefined && (
                      <JobArchitectureStats
                          getStatsContent={getStatsContent}
                          statistics={architecture.statistics}
                          variant="item"
                      />
                  )}
        </CommandPaletteItem>
    );

    return (
        <Root open={isOpen} onOpenChange={setOpen}>
            {triggerVariant === 'button' ? (
                <Trigger
                    aria-describedby={ariaDescribedBy}
                    aria-label={ariaLabel}
                    asChild={true}
                    disabled={disabled}
                    ref={triggerRef}
                    role="combobox"
                >
                    <Button
                        className={triggerClassName}
                        disabled={disabled}
                        iconPosition="trailing"
                        isActive={isOpen}
                        size="sm"
                        variant="secondary"
                    >
                        <span className="min-w-0 truncate">{currentArchitecture?.title ?? placeholder}</span>
                        <IconChevronGrabberVertical className="shrink-0" size={16} />
                    </Button>
                </Trigger>
            ) : (
                <Trigger
                    aria-describedby={ariaDescribedBy}
                    aria-label={ariaLabel}
                    disabled={disabled}
                    ref={triggerRef}
                    className={clsx(
                        'flex flex-col shrink-0 border-none rounded-lg cursor-pointer font-sans outline-none w-full',
                        'transition-[background-color] duration-200 ease-in-out',
                        'focus-visible:outline focus-visible:outline-1 focus-visible:outline-focus-ring',
                        'disabled:cursor-default disabled:opacity-50',
                        size === 'l' ? 'gap-s py-l px-xxl' : 'py-s px-m',
                        // Without a statistics row the title would sit high in the trigger, so centre it
                        // and hold the height the row would have occupied.
                        !showsTriggerStats && 'justify-center min-h-[2.625rem]',
                    )}
                    role="combobox"
                    style={{
                        backgroundColor: triggerBackgroundColor ?? 'var(--semantic-surface-raised)',
                    }}
                >
                    <div className="flex items-center gap-xs w-full">
                        <div className="text-text-content-strong text-body-s font-medium text-left">
                            {currentArchitecture?.title ?? placeholder}
                        </div>
                        <div className="text-text-content-subtle h-[16px] w-[16px] transition-transform duration-200 ease-in-out">
                            <IconChevronGrabberVertical size={16} />
                        </div>
                    </div>
                    {showsTriggerStats && (
                        <JobArchitectureStats
                            className="w-full mt-l"
                            getStatsContent={getStatsContent}
                            statistics={currentArchitecture?.statistics}
                            variant="trigger"
                        />
                    )}
                </Trigger>
            )}
            <Portal>
                <Content
                    align="start"
                    className={clsx(
                        floatingSurfaceClassNames.tone.white,
                        'flex flex-col max-h-[min(32rem,var(--radix-popover-content-available-height))] overflow-hidden w-[32rem] z-[99999]',
                        'rounded-xl outline-none',
                        'data-[side=bottom]:animate-slide-up-fade data-[side=top]:animate-slide-down-fade',
                    )}
                    data-color-scheme={colorScheme}
                    sideOffset={4}
                    onOpenAutoFocus={(event) => {
                        if (!isSearchable) {
                            event.preventDefault();
                            commandRef.current?.focus();
                        }
                    }}
                >
                    <CommandPalette filter={matchesTitle} label={ariaLabel} loop={true} ref={commandRef} tabIndex={-1}>
                        {isSearchable && <CommandPaletteSearch placeholder={labels.search} />}
                        {isOpen && onVisibleArchitectureIdsChange !== undefined && (
                            <VisibleArchitectures commandRef={commandRef} onChange={onVisibleArchitectureIdsChange} />
                        )}
                        <CommandPaletteList>
                            <CommandPaletteEmpty>{labels.empty}</CommandPaletteEmpty>
                            {current !== undefined && (
                                <CommandPaletteGroup heading={labels.current}>
                                    {renderItem(current, true)}
                                </CommandPaletteGroup>
                            )}
                            {others.length > 0 && (
                                <CommandPaletteGroup heading={labels.all}>
                                    {others.map((architecture) => renderItem(architecture, false))}
                                </CommandPaletteGroup>
                            )}
                        </CommandPaletteList>
                        {onCreateNewArchitecture !== undefined && (
                            <CommandPaletteFooter>
                                <Button
                                    className="w-full justify-start"
                                    iconPosition="leading"
                                    type="button"
                                    variant="tertiary"
                                    onClick={onCreateNewArchitecture}
                                >
                                    <IconPlus size={16} />
                                    {createNewArchitecture}
                                </Button>
                            </CommandPaletteFooter>
                        )}
                    </CommandPalette>
                </Content>
            </Portal>
        </Root>
    );
};
